// ============================================================
//  src/pages/bots/tabs/UsersTab.js  — Enhanced Edition
//  Responsive · Confirm modals · Mobile-first cards
// ============================================================
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listBotUsers, blockBotUser, unblockBotUser,
  sendBotUserMsg, broadcastBotMsg, exportBotUsers,
  listCommands, getBillingOverview,
} from '../../../api/client';
import toast from 'react-hot-toast';
import './tabs.css';
import './UsersTab.css';

function timeAgo(dateStr) {
  if (!dateStr) return '—';
  const d    = new Date(dateStr.replace(' ', 'T'));
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60)    return 'just now';
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function validateButtons(buttons) {
  for (const b of buttons) {
    if (!b.title.trim()) return 'All buttons must have a label';
    if (b.type === 'url') {
      if (!b.value.trim()) return 'URL buttons must have a URL';
      try { new URL(b.value.trim()); } catch { return 'Invalid URL in button'; }
    }
    if (b.type === 'command' && !b.value.trim())
      return 'Command buttons must have a command (e.g. /data)';
  }
  const urlCount = buttons.filter(b => b.type === 'url').length;
  if (urlCount > 1) return 'Only one URL button is allowed per message';
  if (urlCount > 0 && buttons.some(b => b.type !== 'url'))
    return 'URL buttons cannot be mixed with reply/command buttons';
  return null;
}

function prepareButtons(buttons) {
  return buttons.map(b => ({
    type:  b.type,
    title: b.title.trim(),
    id:    b.type === 'command'
             ? b.value.trim()
             : b.title.toLowerCase().replace(/\s+/g, '_').slice(0, 256),
    ...(b.type === 'url' ? { url: b.value.trim() } : {}),
  }));
}

// ── Confirm Modal ─────────────────────────────────────────────
function ConfirmModal({ title, message, detail, confirmLabel = 'Confirm', variant = 'danger', onConfirm, onClose }) {
  const ICONS = { danger: '🗑️', warning: '⚠️', info: 'ℹ️' };
  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="ut-confirm-backdrop" onClick={onClose}>
      <div className={`ut-confirm-dialog ${variant}`} onClick={e => e.stopPropagation()}>
        <div className="ut-confirm-header">
          <div className={`ut-confirm-icon-wrap ${variant}`}>{ICONS[variant]}</div>
          <div className="ut-confirm-header-text">
            <div className="ut-confirm-title">{title}</div>
            {message && <div className="ut-confirm-message">{message}</div>}
          </div>
        </div>
        {detail && (
          <div className="ut-confirm-body">
            <div className="ut-confirm-detail">{detail}</div>
          </div>
        )}
        <div className="ut-confirm-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button
            className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => { onConfirm(); onClose(); }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Command autocomplete input ────────────────────────────────
function CommandInput({ value, onChange, commands }) {
  const [open,      setOpen]      = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const filtered = commands.filter(c =>
    c.cType === 'command' &&
    c.cTrigger.toLowerCase().includes((value || '').toLowerCase())
  );

  useEffect(() => {
    const handler = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const select = (trigger) => { onChange(trigger); setOpen(false); };

  const handleKey = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlight]) select(filtered[highlight].cTrigger); }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className="form-input"
        placeholder="Type / to pick a command"
        value={value}
        onChange={e => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => { if (value === '' || value.startsWith('/')) setOpen(true); }}
        onKeyDown={handleKey}
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="ut-cmd-dropdown">
          {filtered.map((cmd, i) => (
            <div
              key={cmd.cId}
              onMouseDown={() => select(cmd.cTrigger)}
              onMouseEnter={() => setHighlight(i)}
              className={`ut-cmd-option ${i === highlight ? 'highlighted' : ''}`}
              style={{ borderBottom: i < filtered.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span className="ut-cmd-trigger">{cmd.cTrigger}</span>
              <span className="ut-cmd-type-tag">{cmd.cType}</span>
              <span className="ut-cmd-lang">{cmd.cLanguage}</span>
            </div>
          ))}
        </div>
      )}
      {open && value.startsWith('/') && filtered.length === 0 && (
        <div className="ut-cmd-dropdown" style={{ padding: '10px 14px', fontSize: 12.5, color: 'var(--text-muted)' }}>
          No matching commands found.
        </div>
      )}
    </div>
  );
}

// ── Reusable Button Builder ───────────────────────────────────
function ButtonBuilder({ buttons, onChange, commands }) {
  const add    = () => { if (buttons.length >= 3) return; onChange([...buttons, { type: 'reply', title: '', value: '' }]); };
  const remove = (i) => onChange(buttons.filter((_, idx) => idx !== i));
  const update = (i, fields) => onChange(buttons.map((b, idx) => idx === i ? { ...b, ...fields } : b));

  const hasUrl    = buttons.some(b => b.type === 'url');
  const hasNonUrl = buttons.some(b => b.type !== 'url');

  return (
    <div className="ut-btn-builder">
      <div className="ut-btn-builder-header">
        <span className="ut-btn-builder-label">Buttons <span className="ut-btn-builder-hint">(optional · max 3)</span></span>
        {buttons.length < 3 && (
          <button type="button" className="btn btn-outline" style={{ padding: '4px 10px', fontSize: 12 }} onClick={add}>
            + Add Button
          </button>
        )}
      </div>

      {buttons.length === 0 && (
        <div className="ut-btn-builder-empty">No buttons — message will be sent as plain text.</div>
      )}

      {buttons.map((btn, i) => (
        <div key={i} className="ut-btn-card">
          <div className="ut-btn-card-row">
            <div className="ut-btn-number">{i + 1}</div>
            <input
              className="form-input"
              placeholder="Button label (max 20 chars)"
              maxLength={20}
              value={btn.title}
              onChange={e => update(i, { title: e.target.value })}
              style={{ flex: 1 }}
            />
            <button type="button" className="icon-btn danger" title="Remove" onClick={() => remove(i)}>✕</button>
          </div>

          <div className="ut-btn-type-row">
            {[
              { key: 'reply',   label: '💬 Reply',   tip: 'Sends label as a message' },
              { key: 'command', label: '⌨️ Command', tip: 'Triggers a bot command', disabled: hasUrl },
              { key: 'url',     label: '🔗 URL',     tip: 'Opens a link (solo only)', disabled: hasNonUrl && btn.type !== 'url' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                title={opt.tip}
                disabled={opt.disabled}
                onClick={() => update(i, { type: opt.key, value: '' })}
                className={`ut-btn-type-opt ${btn.type === opt.key ? 'active' : ''} ${opt.disabled ? 'disabled' : ''}`}>
                {opt.label}
              </button>
            ))}
          </div>

          {btn.type === 'command' && (
            <CommandInput value={btn.value} onChange={val => update(i, { value: val })} commands={commands} />
          )}
          {btn.type === 'url' && (
            <input className="form-input" placeholder="https://example.com" value={btn.value}
              onChange={e => update(i, { value: e.target.value })} />
          )}
        </div>
      ))}

      {hasUrl && (
        <div className="ut-hint-warn">⚠️ URL button cannot be combined with other button types (WhatsApp limitation).</div>
      )}
      {!hasUrl && buttons.length > 0 && (
        <div className="ut-hint-info">💡 <b>Reply</b> sends the label as a message. <b>Command</b> triggers a bot command. <b>URL</b> opens a link (solo only).</div>
      )}
    </div>
  );
}

// ── Preview Bubble ────────────────────────────────────────────
function PreviewBubble({ msg, buttons }) {
  if (!msg.trim() && buttons.length === 0) return (
    <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Start typing to preview.</div>
  );
  return (
    <div>
      <div className="ut-preview-bubble">
        {msg || <span style={{ opacity: 0.4 }}>Your message…</span>}
      </div>
      {buttons.map((b, i) => (
        <div key={i} className={`ut-preview-btn ut-preview-btn-${b.type}`}>
          {b.type === 'url' ? '🔗' : b.type === 'command' ? '⌨️' : '💬'}
          {b.title || <span style={{ opacity: 0.4 }}>Button {i + 1}</span>}
          {b.type === 'url' && b.value && (
            <span className="ut-preview-btn-sub">
              {(() => { try { return new URL(b.value).hostname; } catch { return b.value; } })()}
            </span>
          )}
          {b.type === 'command' && b.value && (
            <span className="ut-preview-btn-sub ut-preview-btn-cmd">→ {b.value}</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Send Message Modal ────────────────────────────────────────
function SendModal({ user, bId, commands, onClose }) {
  const [msg,     setMsg]     = useState('');
  const [buttons, setButtons] = useState([]);
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (!msg.trim()) return;
    const err = validateButtons(buttons);
    if (err) { toast.error(err); return; }
    setSending(true);
    try {
      await sendBotUserMsg(bId, user.uId, msg.trim(), prepareButtons(buttons));
      toast.success('Message sent');
      onClose();
    } catch { toast.error('Failed to send message'); }
    finally  { setSending(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">💬 Send Message</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="ut-send-to">
            To: <strong>{user.uName || user.uChatId}</strong>
          </div>
          <textarea
            className="form-input"
            rows={4}
            style={{ resize: 'vertical' }}
            placeholder="Type your message…"
            value={msg}
            onChange={e => setMsg(e.target.value)}
            autoFocus
          />
          <ButtonBuilder buttons={buttons} onChange={setButtons} commands={commands} />
          {(msg.trim() || buttons.length > 0) && (
            <div className="ut-preview-wrap">
              <div className="ut-preview-label">Preview</div>
              <PreviewBubble msg={msg} buttons={buttons} />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={send} disabled={sending || !msg.trim()}>
            {sending ? <><span className="spinner" /> Sending…</> : '📤 Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Broadcast Modal ───────────────────────────────────────────
function BroadcastModal({ bId, total, commands, onClose }) {
  const [msg,     setMsg]     = useState('');
  const [buttons, setButtons] = useState([]);
  const [sending, setSending] = useState(false);
  const [result,  setResult]  = useState(null);
  const [confirm, setConfirm] = useState(false);

  const send = async () => {
    const err = validateButtons(buttons);
    if (err) { toast.error(err); return; }
    setSending(true);
    try {
      const res = await broadcastBotMsg(bId, msg.trim(), prepareButtons(buttons));
      setResult(res.data);
      toast.success(`Broadcast complete — ${res.data.sent} sent`);
    } catch { toast.error('Broadcast failed'); }
    finally  { setSending(false); }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">📢 Broadcast to All Users</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {result ? (
            <div className="ut-broadcast-result">
              <div style={{ fontSize: 36, marginBottom: 10 }}>✅</div>
              <div className="ut-broadcast-result-title">Broadcast Complete</div>
              <div className="ut-broadcast-result-sub">
                {result.sent} sent · {result.failed} failed · {result.total} total
              </div>
              <button className="btn btn-outline" style={{ marginTop: 20 }} onClick={onClose}>Close</button>
            </div>
          ) : (
            <div className="ut-broadcast-grid">
              <div className="ut-broadcast-form">
                <div className="ut-send-to" style={{ marginBottom: 12 }}>
                  Sending to <strong>{total}</strong> non-blocked users
                </div>
                <div className="ut-hint-warn" style={{ marginBottom: 14 }}>
                  ⚠️ Keep broadcasts infrequent to avoid Meta rate limits.
                </div>
                <textarea
                  className="form-input"
                  rows={5}
                  style={{ resize: 'vertical' }}
                  placeholder="Type your broadcast message…"
                  value={msg}
                  onChange={e => setMsg(e.target.value)}
                  autoFocus
                />
                <ButtonBuilder buttons={buttons} onChange={setButtons} commands={commands} />
              </div>
              <div className="ut-broadcast-preview">
                <div className="ut-preview-label">Preview</div>
                <div className="ut-broadcast-preview-box">
                  <PreviewBubble msg={msg} buttons={buttons} />
                </div>
              </div>
            </div>
          )}
        </div>
        {!result && (
          <div className="modal-footer">
            <button className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={() => setConfirm(true)} disabled={sending || !msg.trim()}>
              {sending ? <><span className="spinner" /> Broadcasting…</> : `📢 Send to ${total} users`}
            </button>
          </div>
        )}
      </div>

      {confirm && (
        <ConfirmModal
          title={`Broadcast to ${total} users?`}
          message="This message will be sent to all non-blocked users immediately. This action cannot be undone."
          confirmLabel={`📢 Send to ${total} users`}
          variant="warning"
          onConfirm={send}
          onClose={() => setConfirm(false)}
        />
      )}
    </div>
  );
}

// ── User Card (mobile view) ───────────────────────────────────
function UserCard({ u, blocking, onSend, onToggleBlock, canBroadcast, navigate }) {
  const [confirm, setConfirm] = useState(null);

  function askToggle() {
    setConfirm({
      title: u.uIsBlocked ? `Unblock ${u.uName || 'user'}?` : `Block ${u.uName || 'user'}?`,
      message: u.uIsBlocked
        ? 'This user will be able to interact with your bot again.'
        : 'This user will no longer be able to receive messages from your bot.',
      detail: u.uChatId,
      confirmLabel: u.uIsBlocked ? '✅ Unblock' : '🚫 Block',
      variant: u.uIsBlocked ? 'info' : 'danger',
    });
  }

  return (
    <>
      <div className={`ut-user-card ${u.uIsBlocked ? 'blocked' : ''}`}>
        <div className="ut-user-card-left">
          <div className={`user-avatar-sm ${u.uIsBlocked ? 'avatar-blocked' : ''}`}>
            {(u.uName?.[0] ?? u.uChatId?.[0] ?? '?').toUpperCase()}
          </div>
          <div className="ut-user-card-info">
            <div className="ut-user-card-name">
              {u.uName || <span style={{ color: 'var(--text-muted)' }}>Anonymous</span>}
            </div>
            <div className="ut-user-card-meta">
              <span className="mono" style={{ fontSize: 11 }}>{u.uChatId}</span>
              <span className="ut-user-card-dot">·</span>
              <span>{u.uMsgsCount ?? 0} msgs</span>
              <span className="ut-user-card-dot">·</span>
              <span>{timeAgo(u.uLastSeenAt)}</span>
            </div>
          </div>
        </div>
        <div className="ut-user-card-right">
          <span className={`status-pill ${u.uIsBlocked ? 'blocked-pill' : 'active-pill'}`}>
            {u.uIsBlocked ? 'Blocked' : 'Active'}
          </span>
          <div className="ut-user-card-actions">
            {!u.uIsBlocked && (
              canBroadcast
                ? <button className="icon-btn" title="Send message" onClick={() => onSend(u)}>💬</button>
                : <button className="icon-btn" title="Upgrade to send messages" onClick={() => navigate('/billing')} style={{ opacity: 0.5 }}>🔒</button>
            )}
            <button
              className={`icon-btn ${u.uIsBlocked ? '' : 'danger'}`}
              title={u.uIsBlocked ? 'Unblock user' : 'Block user'}
              disabled={!!blocking[u.uId]}
              onClick={askToggle}>
              {blocking[u.uId]
                ? <span className="spinner spinner-dark" style={{ width: 11, height: 11 }} />
                : u.uIsBlocked ? '✅' : '🚫'}
            </button>
          </div>
        </div>
      </div>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          detail={confirm.detail}
          confirmLabel={confirm.confirmLabel}
          variant={confirm.variant}
          onConfirm={() => onToggleBlock(u)}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ── Main UsersTab ─────────────────────────────────────────────
export default function UsersTab({ bId }) {
  const navigate      = useNavigate();
  const [users,       setUsers]       = useState([]);
  const [total,       setTotal]       = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [commands,    setCommands]    = useState([]);
  const [search,      setSearch]      = useState('');
  const [sendUser,    setSendUser]    = useState(null);
  const [showBcast,   setShowBcast]   = useState(false);
  const [exporting,   setExporting]   = useState(false);
  const [blocking,    setBlocking]    = useState({});
  const [canBroadcast, setCanBroadcast] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      listBotUsers(bId),
      listCommands(bId),
      getBillingOverview(),
    ]).then(([usersRes, cmdsRes, billingRes]) => {
      if (usersRes.data?.success) {
        setUsers(usersRes.data.users ?? []);
        setTotal(usersRes.data.total ?? 0);
      }
      if (cmdsRes.data?.success) setCommands(cmdsRes.data.commands ?? []);
      const plan = billingRes?.data?.current_plan;
      setCanBroadcast(!!plan?.plAllowBroadcast);
    })
    .catch(() => toast.error('Failed to load users'))
    .finally(() => setLoading(false));
  }, [bId]);

  useEffect(() => { load(); }, [load]);

  const toggleBlock = async (u) => {
    const isBlocked = !!u.uIsBlocked;
    setBlocking(b => ({ ...b, [u.uId]: true }));
    try {
      if (isBlocked) {
        await unblockBotUser(bId, u.uId);
        toast.success(`${u.uName || 'User'} unblocked`);
      } else {
        await blockBotUser(bId, u.uId);
        toast.success(`${u.uName || 'User'} blocked`);
      }
      setUsers(prev =>
        prev.map(x => x.uId === u.uId ? { ...x, uIsBlocked: isBlocked ? 0 : 1 } : x)
      );
    } catch { toast.error('Action failed'); }
    finally  { setBlocking(b => { const n = { ...b }; delete n[u.uId]; return n; }); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await exportBotUsers(bId);
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `users_${bId}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch { toast.error('Export failed'); }
    finally  { setExporting(false); }
  };

  const filtered = users.filter(u =>
    !search ||
    (u.uName   ?? '').toLowerCase().includes(search.toLowerCase()) ||
    (u.uChatId ?? '').toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="tab-loading"><span className="spinner spinner-dark" /> Loading users…</div>
  );

  return (
    <div className="tab-content">
      {/* ── Toolbar ── */}
      <div className="tab-toolbar">
        <div>
          <div className="tab-title">Bot Users</div>
          <div className="tab-sub">{total} total user{total !== 1 ? 's' : ''}</div>
        </div>
        <div className="ut-toolbar-actions">
          <input
            className="form-input ut-search"
            placeholder="🔍 Search users…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <button className="btn btn-outline" onClick={handleExport} disabled={exporting || total === 0}>
            {exporting ? <span className="spinner spinner-dark" /> : '⬇️'} <span className="ut-btn-label">Export CSV</span>
          </button>
          {canBroadcast ? (
            <button className="btn btn-primary" onClick={() => setShowBcast(true)} disabled={total === 0}>
              📢 <span className="ut-btn-label">Broadcast</span>
            </button>
          ) : (
            <button className="btn btn-outline" onClick={() => navigate('/billing')}
              title="Upgrade your plan to broadcast messages" style={{ color: 'var(--text-muted)' }}>
              🔒 <span className="ut-btn-label">Broadcast</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Empty state ── */}
      {filtered.length === 0 ? (
        <div className="tab-empty">
          {users.length === 0
            ? 'No users yet. Users appear here once they interact with your bot.'
            : 'No users match your search.'}
        </div>
      ) : (
        <>
          {/* ── Desktop table ── */}
          <div className="users-table-wrap ut-table-desktop">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Chat ID</th>
                  <th>Messages</th>
                  <th>Last seen</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <TableRow
                    key={u.uId}
                    u={u}
                    blocking={blocking}
                    canBroadcast={canBroadcast}
                    onSend={setSendUser}
                    onToggleBlock={toggleBlock}
                    navigate={navigate}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Mobile card list ── */}
          <div className="ut-cards-mobile">
            {filtered.map(u => (
              <UserCard
                key={u.uId}
                u={u}
                blocking={blocking}
                canBroadcast={canBroadcast}
                onSend={setSendUser}
                onToggleBlock={toggleBlock}
                navigate={navigate}
              />
            ))}
          </div>
        </>
      )}

      {sendUser && (
        <SendModal user={sendUser} bId={bId} commands={commands} onClose={() => setSendUser(null)} />
      )}
      {showBcast && (
        <BroadcastModal bId={bId} total={total} commands={commands} onClose={() => setShowBcast(false)} />
      )}
    </div>
  );
}

// ── Desktop table row with confirm modal ──────────────────────
function TableRow({ u, blocking, canBroadcast, onSend, onToggleBlock, navigate }) {
  const [confirm, setConfirm] = useState(null);

  function askToggle() {
    setConfirm({
      title: u.uIsBlocked ? `Unblock ${u.uName || 'user'}?` : `Block ${u.uName || 'user'}?`,
      message: u.uIsBlocked
        ? 'This user will be able to interact with your bot again.'
        : 'This user will no longer be able to receive messages from your bot.',
      detail: u.uChatId,
      confirmLabel: u.uIsBlocked ? '✅ Unblock' : '🚫 Block',
      variant: u.uIsBlocked ? 'info' : 'danger',
    });
  }

  return (
    <>
      <tr style={{ opacity: u.uIsBlocked ? 0.6 : 1 }}>
        <td>
          <div className="user-name-cell">
            <div className={`user-avatar-sm ${u.uIsBlocked ? 'avatar-blocked' : ''}`}>
              {(u.uName?.[0] ?? u.uChatId?.[0] ?? '?').toUpperCase()}
            </div>
            <span>{u.uName || <span style={{ color: 'var(--text-muted)' }}>Anonymous</span>}</span>
          </div>
        </td>
        <td><span className="mono" style={{ fontSize: 12 }}>{u.uChatId}</span></td>
        <td>{u.uMsgsCount ?? 0}</td>
        <td>{timeAgo(u.uLastSeenAt)}</td>
        <td>
          <span className={`status-pill ${u.uIsBlocked ? 'blocked-pill' : 'active-pill'}`}>
            {u.uIsBlocked ? 'Blocked' : 'Active'}
          </span>
        </td>
        <td>
          <div style={{ display: 'flex', gap: 6 }}>
            {!u.uIsBlocked && (
              canBroadcast
                ? <button className="icon-btn" title="Send message" onClick={() => onSend(u)}>💬</button>
                : <button className="icon-btn" title="Upgrade to send messages" onClick={() => navigate('/billing')} style={{ opacity: 0.5 }}>🔒</button>
            )}
            <button
              className={`icon-btn ${u.uIsBlocked ? '' : 'danger'}`}
              title={u.uIsBlocked ? 'Unblock user' : 'Block user'}
              disabled={!!blocking[u.uId]}
              onClick={askToggle}>
              {blocking[u.uId]
                ? <span className="spinner spinner-dark" style={{ width: 11, height: 11 }} />
                : u.uIsBlocked ? '✅' : '🚫'}
            </button>
          </div>
        </td>
      </tr>

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          detail={confirm.detail}
          confirmLabel={confirm.confirmLabel}
          variant={confirm.variant}
          onConfirm={() => onToggleBlock(u)}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
}